/*
 * Copyright (c) 2020. American Express Company
 */

import { InstitutionModel } from './institution.model';
import { LocalDateTime } from '@js-joda/core';
import DateUtils from 'src/utils/date';

export class AccountModel {
    id: number;
    accountId: string;
    institutionId: number;
    embossedName: string;
    name: string;
    officialName: string;
    available: number;
    current: number;
    limit: number;
    currencyCode: string;
    type: string;
    subtype: string;
    mask: string;
    institution: InstitutionModel;
    created: LocalDateTime;
    updated: LocalDateTime;
    balanceHistory: AccountBalanceHistory[];
    hidden?: boolean;

    public static deserializeDates(model: AccountModel): AccountModel {
        model.created = model.created && DateUtils.datetimeify(model.created);
        model.updated = model.updated && DateUtils.datetimeify(model.updated);
        if (model.institution) {
            model.institution.created = model.institution.created && DateUtils.datetimeify(model.institution.created);
            model.institution.updated = model.institution.updated && DateUtils.datetimeify(model.institution.updated);
        }
        return model;
    }

    public static deserialize(input: string): AccountModel {
        const model: AccountModel = JSON.parse(input);
        return this.deserializeDates(model);
    }

    public static serialize(model: AccountModel): string {
        return JSON.stringify(this);
    }
}

/** All the types of account drawers */
export enum AccountDrawerType {
    CashAndBanking = 'Cash & Banking',
    InvestmentsAndRetirements = 'Investments & Retirement',
    PropertyAndAssets = 'Property & Asset',
    LoanAndDebt = 'Loan & Debt'
}

/** All the connected account subtypes */
export enum ConnectedAccountSubtype {
    // cash and banking
    Checking = 'checking',
    Savings = 'savings',

    // investments and retirements
    _529 = '529',
    CD = 'cd',
    PrivateStock = 'stock plan',
    _401K = '401k',
    _403B = '403b',
    IRA = 'ira',
    Pension = 'pension',

    // loan and debt
    Mortgage = 'mortgage',
    HomeEquityLoan = 'home equity',
    StudentLoan = 'student',
    AutoLoan = 'auto',
    CreditCard = 'credit card'
}

export interface AccountsDict {
    [accountId: string]: { account: AccountModel; hidden: boolean };
}

export interface BalanceHistory {
    date: string;
    balance: number;
}

export interface AccountBalanceHistory {
    id: number;
    date: string;
    amount: number;
}
